import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  ExternalLink,
  Eye,
  LoaderCircle,
  Sparkles,
  Wand2
} from 'lucide-react';
import { fetchPublicProjectByIdApi } from '../../api/publicProjects.api';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { useI18n } from '../../hooks/useI18n';

const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || '?';

export const PublicTemplateDetailModal = ({
  open,
  projectId,
  fallbackProject,
  actionLoading = false,
  actionError = '',
  onClose,
  onUseTemplate
}) => {
  const { t } = useI18n();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [coverFailed, setCoverFailed] = useState(false);

  useEffect(() => {
    if (!open || !projectId) {
      return undefined;
    }

    let cancelled = false;

    const loadProject = async () => {
      setLoading(true);
      setError('');
      setCoverFailed(false);
      setProject(fallbackProject || null);

      try {
        const details = await fetchPublicProjectByIdApi(projectId);

        if (!cancelled) {
          setProject(details);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message || t('publicTemplatesDetailError'));
          setProject(fallbackProject || null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProject();

    return () => {
      cancelled = true;
    };
  }, [fallbackProject, open, projectId, t]);

  const handleClose = () => {
    if (loading || actionLoading) {
      return;
    }

    onClose();
  };

  const activeProject = project || fallbackProject;
  const showCover = Boolean(activeProject?.coverImageUrl) && !coverFailed;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      closeDisabled={loading || actionLoading}
      title={activeProject?.title || t('publicTemplatesDetailTitle')}
      panelClassName="max-w-3xl rounded-[1.75rem] p-0 overflow-hidden"
      contentClassName="mb-0 border-b border-border px-5 py-4 md:px-6"
      titleClassName="text-base md:text-lg"
    >
      <div className="max-h-[min(78vh,720px)] overflow-y-auto px-5 pt-4 pb-5 md:px-6 md:pt-5 md:pb-6">
        {loading && !activeProject ? (
          <div className="flex min-h-[220px] items-center justify-center gap-2 text-sm text-muted">
            <LoaderCircle size={18} className="animate-spin text-accent" />
            {t('publicTemplatesDetailLoading')}
          </div>
        ) : null}

        {error ? (
          <div className="mb-4 flex gap-3 rounded-2xl border border-amber-200/80 bg-amber-50/80 p-4 text-sm text-amber-900 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-100">
            <AlertCircle size={18} className="mt-0.5 shrink-0" />
            <p className="leading-6">{error}</p>
          </div>
        ) : null}

        {activeProject ? (
          <div className="space-y-5">
            <div className="relative overflow-hidden rounded-[1.25rem] border border-border bg-gradient-to-br from-slate-100 via-accent-soft/30 to-teal-100/40 dark:from-slate-900 dark:via-accent/10 dark:to-slate-900">
              {showCover ? (
                <img
                  alt=""
                  className="aspect-[16/9] w-full object-cover"
                  onError={() => setCoverFailed(true)}
                  src={activeProject.coverImageUrl}
                />
              ) : (
                <div className="flex aspect-[16/9] items-center justify-center text-muted">
                  <Sparkles size={28} className="text-accent" />
                </div>
              )}
            </div>

            <p className="text-sm leading-7 text-muted">
              {activeProject.description?.trim() || t('newProjectHint')}
            </p>

            <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-border/80 bg-white/45 p-4 dark:bg-slate-950/20">
              <div className="flex min-w-0 items-center gap-3">
                {activeProject.owner?.avatarUrl ? (
                  <img
                    alt=""
                    className="h-10 w-10 rounded-full border border-border object-cover"
                    src={activeProject.owner.avatarUrl}
                  />
                ) : (
                  <span className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-accent-soft text-xs font-bold text-accent">
                    {getInitials(activeProject.owner?.fullName)}
                  </span>
                )}
                <div className="min-w-0">
                  <p className="text-xs text-muted">{t('publicTemplatesDetailAuthor')}</p>
                  <p className="truncate text-sm font-semibold text-text">
                    {activeProject.owner?.fullName || t('publicTemplatesUnknownAuthor')}
                  </p>
                </div>
              </div>

              {typeof activeProject.viewCount === 'number' ? (
                <div className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-muted">
                  <Eye size={14} />
                  {t('publicTemplatesViewCount', { count: activeProject.viewCount })}
                </div>
              ) : null}
            </div>

            {actionError ? (
              <div className="flex gap-3 rounded-2xl border border-rose-200/80 bg-rose-50/80 p-4 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200">
                <AlertCircle size={18} className="mt-0.5 shrink-0" />
                <p className="leading-6">{actionError}</p>
              </div>
            ) : null}

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              {activeProject.deploymentUrl ? (
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full sm:w-auto"
                  disabled={actionLoading}
                  onClick={() => window.open(activeProject.deploymentUrl, '_blank', 'noopener,noreferrer')}
                >
                  <ExternalLink size={16} />
                  {t('publicTemplatesViewLive')}
                </Button>
              ) : null}

              <Button
                type="button"
                className="w-full sm:w-auto"
                disabled={loading || actionLoading || Boolean(error && !fallbackProject)}
                onClick={() => onUseTemplate(activeProject)}
              >
                {loading || actionLoading ? (
                  <>
                    <LoaderCircle size={16} className="animate-spin" />
                    {actionLoading ? t('publicTemplatesCloning') : t('publicTemplatesDetailLoading')}
                  </>
                ) : (
                  <>
                    <Wand2 size={16} />
                    {t('publicTemplatesUseSample')}
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
};
